import { memo } from 'react';
import styled from 'styled-components';
import { shortScreen } from '../learn_screen_parts.tsx';

// The pieces the Range Finder screens share (issues #3481, #3510, #3518), from the "Range Finder"
// artboard: the three wordless progress segments under the header, and the "Hearing you" meter,
// which the other listening exercises share too and so lives in learn_meter.tsx; it is re-exported
// here under its Range Finder names so the range screens keep importing it from this file. The
// meter belongs to the screens that are really listening - the direction screen, in both its
// halves - so it is always live and carries no margin of its own: the direction screen spaces it
// with slacks of its own.

export {
  HearingYouMeter,
  learnMeterFillTestId as rangeMeterFillTestId,
  learnMeterTestId as rangeMeterTestId,
} from '../learn_meter.tsx';

export const rangeProgressRowTestId = 'range-progress';
export const rangeProgressSegmentTestId = 'range-progress-segment';

// Three steps: the microphone, going lower, going higher (issue #3510)
export const rangeProgressSegmentCount = 3;

// The wordless progress bar under the header (issue #3510, the Rhythm Echo pips scaled up since
// these carry no text): segments are filled through the current one, so `segment` 2 fills two.
// Memoised because the page renders it on every microphone frame, and it changes three times a run.
export const RangeProgressSegments = memo(({ segment }: { segment: number }) => (
  <ProgressRow data-testid={rangeProgressRowTestId} data-segment={segment}>
    {Array.from({ length: rangeProgressSegmentCount }, (_unused, index) => {
      const filled = index < segment;
      return (
        <ProgressSegment
          key={index}
          $filled={filled}
          data-testid={rangeProgressSegmentTestId}
          data-state={filled ? 'filled' : 'todo'}
          data-index={index + 1}
        />
      );
    })}
  </ProgressRow>
));
RangeProgressSegments.displayName = 'RangeProgressSegments';

const ProgressRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 6px;
  padding: 10px 0 2px; // clear of the header's rule above

  ${shortScreen} {
    padding: 6px 0 2px;
  }
`;

const ProgressSegment = styled.div<{ $filled: boolean }>`
  width: 48px;
  height: 6px;
  border-radius: 3px;
  background-color: ${({ $filled }) => ($filled ? 'var(--color-tab-selected)' : 'var(--color-divider)')};
`;

// The illustration circle on the permission and result screens (72 px, the canvas's grey disc)
export const IllustrationCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background-color: var(--color-background-search);
  color: var(--color-tab-selected);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 32px;
    height: 32px;
  }

  ${shortScreen} {
    width: 60px;
    height: 60px;

    svg {
      width: 26px;
      height: 26px;
    }
  }
`;
